// @ts-check
// Widgets de relatório em cartão (contrato 07, seção 7): números em destaque, listas de barras com trilho,
// distribuição em faixas de dias, funil com degraus de tempo e análise por etapa com rolagem horizontal.
// As cores dizem o significado: volume (verde água), visita e conversão (amarelo lima), alerta (coral).
// Números, itens, detalhes de contagem, faixas, linhas do funil e blocos por etapa abrem a lista dos registros contados
// (contrato 07, seção 10); medianas e médias de dias não, porque a base delas já abre pela faixa ou pela linha.
import { html } from 'htm/preact';
import { useState } from 'preact/hooks';
import { Tag } from '../../ui/chip.js';
import { Icon } from '../../ui/icon.js';
import { formatMetricValue } from './widget-format.js';
import { DrillLink, useDrilldown } from './drilldown.js';
import { describeStatDelta, describeStatGoal, describeStatSecondary, formatStatValue, statLabelText } from './report-stat-parts.js';
import { hasTranslation, t } from '../../i18n/index.js';

/**
 * @typedef {{ key: string, value: number | string | null, format?: string, rate?: number, total?: number, measure?: string }} ReportDetail
 * @typedef {{ key: string, label: string, value: number | null, format: string, tone?: string, color?: string, details?: ReportDetail[] }} ReportBarItem
 * @typedef {import('./report-stat-parts.js').ReportStat} ReportStat
 * @typedef {{ kind: 'STAT_GROUP', report: string, stats: ReportStat[], summary?: { hiddenStats?: number } }} StatGroupData
 * @typedef {{ kind: 'BAR_LIST', report: string, scale: 'MAX' | 'PERCENT', items: ReportBarItem[], stats?: ReportStat[], summary?: Record<string, any> }} BarListData
 * @typedef {{ key: string, from: number, to: number | null, value: number, share: number, tone?: string, label?: string }} DistributionBucket
 * @typedef {{ kind: 'DISTRIBUTION', report: string, unit?: 'DAYS' | 'PERCENT_BAND', buckets: DistributionBucket[], total: number, stats?: ReportStat[], summary?: Record<string, any> }} DistributionData
 * @typedef {{ count: number, amount: number, share?: number, label?: string }} CountAndAmount
 * @typedef {{ key: string, label: string, color?: string, reached: number, reachedAmount: number, share: number, averageDays: number | null }} FunnelRow
 * @typedef {{ total: number, totalAmount: number, lifecycleDays: number | null, rows: FunnelRow[], won: CountAndAmount, lost: CountAndAmount }} FunnelTab
 * @typedef {{ kind: 'FUNNEL_FLOW', report: string, tabs: Record<'ALL' | 'ACTIVE' | 'CLOSED', FunnelTab> }} FunnelFlowData
 * @typedef {{ key: string, label: string, color?: string, inside: CountAndAmount, entered: CountAndAmount, lost: CountAndAmount }} StageFlowColumn
 * @typedef {{ kind: 'STAGE_FLOW', report: string, highlightStage?: string, columns: StageFlowColumn[] }} StageFlowData
 */

export const REPORT_WIDGET_TYPES = /** @type {const} */ (['STAT_GROUP', 'BAR_LIST', 'DISTRIBUTION', 'FUNNEL_FLOW', 'STAGE_FLOW', 'UPCOMING_TABLE', 'GEO_MAP']);

/** Relatórios que cada tipo sabe desenhar (o mesmo catálogo do motor, src/engine/dashboards/report-catalog.js). */
export const REPORTS_BY_WIDGET_TYPE = /** @type {Record<string, string[]>} */ ({
  STAT_GROUP: ['ADMISSIONS_SUMMARY', 'VISITS_SUMMARY', 'PERIOD_INDICATORS', 'REVENUE_AND_TIME'],
  BAR_LIST: ['WHATSAPP_ORIGIN', 'STAGE_DISTRIBUTION', 'VISITS_BY_SEGMENT', 'INTENDED_GRADE', 'OWNER_PERFORMANCE', 'ORIGIN_SCHOOL', 'LEAD_SOURCE', 'IDLE_BY_STAGE', 'DATA_QUALITY', 'TAGS', 'VISITS_BY_HOST', 'VISITS_BY_TYPE', 'VISITS_BY_WEEKDAY', 'VISITS_BY_HOUR', 'VISIT_CONVERSION',
    'LOST_BY_REASON', 'OCCUPANCY_BY_CLASS', 'LEAD_QUALITY_BY_ENTITY', 'LEAD_QUALITY_FIELDS', 'LEAD_QUALITY_BY_OWNER', 'LEAD_QUALITY_BY_STAGE'],
  DISTRIBUTION: ['FOLLOW_UP_IDLE', 'ENTRY_TO_VISIT', 'VISIT_LEAD_TIME', 'LEAD_QUALITY'],
  FUNNEL_FLOW: ['CONVERSION_FUNNEL'],
  STAGE_FLOW: ['STAGE_SALES'],
  UPCOMING_TABLE: ['UPCOMING_EVENTS'],
  GEO_MAP: ['LEAD_GEOGRAPHY'],
});

/** Relatórios com limite de itens configurável. */
export const LIMITED_REPORTS = new Set(['INTENDED_GRADE', 'ORIGIN_SCHOOL', 'LEAD_SOURCE', 'OWNER_PERFORMANCE', 'TAGS', 'UPCOMING_EVENTS', 'OCCUPANCY_BY_CLASS', 'LEAD_QUALITY_BY_OWNER']);

/** Relatórios que saíram do seletor do editor (continuam válidos nos widgets que já os usam). */
export const DEPRECATED_REPORTS = new Set(['DATA_QUALITY']);

/** Relatórios da qualidade do lead: aceitam a escolha dos campos medidos (e o cadastro, no de campos). */
export const LEAD_QUALITY_REPORTS = new Set(['LEAD_QUALITY', 'LEAD_QUALITY_BY_ENTITY', 'LEAD_QUALITY_FIELDS', 'LEAD_QUALITY_BY_OWNER', 'LEAD_QUALITY_BY_STAGE']);

/** @param {string} type */
export const isReportWidgetType = (type) => /** @type {readonly string[]} */ (REPORT_WIDGET_TYPES).includes(type);

// o subtítulo é puro (report-stat-parts.js), para os testes do cliente
export { getReportSubtitle } from './report-stat-parts.js';

/** @param {ReportStat} stat */
const statLabel = (stat) => (hasTranslation(`reports.stats.${stat.key}`) ? statLabelText(stat) : stat.key);

const DELTA_ICONS = Object.freeze({ up: 'IconTrendingUp', down: 'IconTrendingDown', same: 'IconMinus' });

/**
 * Um número com legenda e, quando o relatório manda, a seta contra o ano anterior, a barra da meta e a legenda
 * secundária (contrato 07, seção 7.1).
 * @param {{ stat: ReportStat }} props
 */
function ReportStatItem({ stat }) {
  const value = formatStatValue(stat);
  const delta = describeStatDelta(stat);
  const goal = describeStatGoal(stat);
  const secondary = describeStatSecondary(stat);
  const label = statLabel(stat);
  // o valor é o link do número e cobre o número inteiro (stretched); o resto que também abre lista fica por cima
  return html`<div class="report-stat" data-tone=${stat.tone ?? 'NEUTRAL'}>
    <${DrillLink} point=${{ part: 'stat', key: stat.key }} label=${label} valueText=${value.text} className="report-stat-value" stretched=${true}
      title=${value.title}>${value.text}<//>
    <span class="report-stat-label">${label}</span>
    ${delta
      ? html`<span class="report-stat-delta" data-trend=${delta.trend} data-quality=${delta.quality} title=${delta.title} aria-label=${delta.ariaLabel}>
          <${Icon} name=${DELTA_ICONS[delta.trend]} size=${12} />
          <span data-length="full">${delta.text}</span><span data-length="short">${delta.shortText}</span>
        </span>`
      : null}
    ${goal
      ? html`<div class="report-stat-goal">
          <div class="report-stat-goal-track" role="progressbar" aria-valuemin=${goal.minimum} aria-valuemax=${goal.maximum} aria-valuenow=${goal.now} title=${goal.title}>
            <span style=${{ width: `${goal.share}%` }} data-complete=${goal.isComplete ? 'true' : undefined}></span>
          </div>
          <${DrillLink} point=${{ part: 'stat', key: stat.key, detailKey: 'goal' }} label=${`${label} · ${t('reports.csv.goalProgress')}`}
            valueText=${formatMetricValue(goal.now, 'NUMBER')} className="report-stat-goal-text" title=${goal.text}
          ><span data-length="full">${goal.text}</span><span data-length="short">${goal.shortText}</span><//>
        </div>`
      : null}
    ${secondary && stat.secondary
      ? html`<${DrillLink} point=${{ part: 'stat', key: stat.key, detailKey: stat.secondary.key }} label=${`${label} · ${secondary}`}
          valueText=${formatMetricValue(typeof stat.secondary.count === 'number' ? stat.secondary.count : Number(stat.secondary.value), 'NUMBER')}
          className="report-stat-secondary">${secondary}<//>`
      : null}
  </div>`;
}

/**
 * Números grandes com legenda pequena.
 * @param {{ stats: ReportStat[], size?: 'large' | 'medium' }} props
 */
export function ReportStats({ stats, size = 'medium' }) {
  if (!stats || stats.length === 0) return null;
  return html`<div class="report-stats" data-size=${size}>
    ${stats.map((stat) => html`<${ReportStatItem} key=${stat.key} stat=${stat} />`)}
  </div>`;
}

/** @param {{ data: StatGroupData }} props */
export function StatGroupWidget({ data }) {
  return html`<div class="report-widget report-stat-group">
    <${ReportStats} stats=${data.stats} size="large" />
    <${ReportFootnotes} report=${data.report} summary=${data.summary} />
  </div>`;
}

/**
 * Texto da linha de baixo de uma barra ("166 visitas · 20,9% · 25 matrículas (3,1%)").
 * @param {ReportDetail[] | undefined} details
 */
export function formatReportDetails(details) {
  return (details ?? []).map(formatReportDetail).filter(Boolean).join(' · ');
}

/**
 * Texto de um detalhe ("166 visitas", "25 matrículas (3,1%)", "média de 4 dias").
 * @param {ReportDetail} detail
 */
export function formatReportDetail(detail) {
  switch (detail.key) {
    case 'visits': return t('reports.details.visits', { count: detail.value ?? 0 });
    case 'noShows': return t('reports.details.noShows', { count: detail.value ?? 0 });
    case 'upcoming': return t('reports.details.upcoming', { count: detail.value ?? 0 });
    case 'leads': return t('reports.details.leads', { count: detail.value ?? 0 });
    case 'enrollments': return t('reports.details.enrollments', { count: detail.value ?? 0, rate: formatMetricValue(detail.rate ?? 0, 'PERCENT') });
    case 'share': return t('reports.details.share', { value: formatMetricValue(detail.value, 'PERCENT') });
    case 'averageDays': return detail.value === null ? '' : t('reports.details.averageDays', { value: formatMetricValue(detail.value, 'DAYS') });
    case 'ratio': return `${formatMetricValue(detail.value, 'NUMBER')}/${formatMetricValue(detail.total ?? 0, 'NUMBER')}`;
    // conversas aguardando só aparecem quando há alguma (o total da escola está no "No período")
    case 'awaitingReply': return detail.value ? t('reports.details.awaitingReply', { count: detail.value }) : '';
    case 'averageFill': return detail.value === null ? '' : t('reports.details.averageFill', { value: formatMetricValue(detail.value, 'PERCENT') });
    case 'topMissing': return detail.measure ? t('reports.details.topMissing', { label: leadQualityMeasureLabel(detail.measure) }) : '';
    case 'reserved': return t('reports.details.reserved', { count: detail.value ?? 0 });
    case 'available': return t('reports.details.available', { count: detail.value ?? 0 });
    case 'status': return detail.value ? String(detail.value) : '';
    // detalhes com texto próprio (reports.details.<chave>, com plural), como os do módulo Atendimento (3.0.0)
    default: return hasTranslation(`reports.details.${detail.key}`) ? t(`reports.details.${detail.key}`, { count: Number(detail.value ?? 0) }) : formatMetricValue(detail.value, detail.format ?? 'NUMBER');
  }
}

/**
 * Linha de detalhes de um item com cada contagem como link próprio (visitas, matrículas, não compareceram...).
 * @param {{ itemKey: string, itemLabel: string, details: ReportDetail[] | undefined }} props
 */
function ReportDetailLinks({ itemKey, itemLabel, details }) {
  const parts = (details ?? []).map((detail) => ({ detail, text: formatReportDetail(detail) })).filter((part) => part.text);
  if (parts.length === 0) return null;
  return html`<div class="report-bar-details">
    ${parts.map(({ detail, text }, index) => html`${index > 0 ? ' · ' : ''}<${DrillLink} key=${detail.key}
      point=${{ part: 'item', key: itemKey, detailKey: detail.key }} label=${`${itemLabel} · ${text}`} valueText=${formatMetricValue(detail.value, detail.format ?? 'NUMBER')}
      className="report-detail-link">${text}<//>`)}
  </div>`;
}

/**
 * Barra horizontal com trilho: rótulo à esquerda, valor à direita e a linha de detalhes opcional. Com um ponto, o
 * rótulo é o link e cobre a barra inteira; os detalhes que também abrem lista ficam por cima dele.
 * @param {{ label: any, valueText: string, valueContent?: any, share: number, tone?: string, color?: string, detailText?: string, details?: any,
 *   point?: import('./drilldown.js').DrilldownPoint, ariaValueText?: string, hint?: string }} props
 */
function ReportBar({ label, valueText, valueContent, share, tone = 'VOLUME', color, detailText, details, point, ariaValueText, hint }) {
  const drill = useDrilldown();
  const isDrillable = Boolean(point && drill?.target(point));
  const labelText = html`<span class="report-bar-label-text" title=${typeof label === 'string' ? label : undefined}>${label}</span>`;
  return html`<li class="report-bar" data-tone=${tone} data-drillable=${isDrillable ? 'true' : undefined}>
    <div class="report-bar-head">
      <span class="report-bar-label">
        ${color ? html`<span class="report-bar-dot" style=${{ background: `var(--t-color-${color}9, currentColor)` }}></span>` : null}
        ${point
          ? html`<${DrillLink} point=${point} label=${typeof label === 'string' ? label : ''} valueText=${ariaValueText ?? valueText} hint=${hint}
              className="report-bar-link" stretched=${true}>${labelText}<//>`
          : labelText}
      </span>
      <span class="report-bar-value">${valueContent ?? valueText}</span>
    </div>
    <div class="report-bar-track" aria-hidden="true"><span class="report-bar-fill" style=${{ width: `${Math.max(0, Math.min(100, share))}%` }}></span></div>
    ${details ?? (detailText ? html`<div class="report-bar-details">${detailText}</div>` : null)}
  </li>`;
}

/**
 * Rótulo de uma medida da qualidade do lead ("lead.leadSource" -> "Origem do lead").
 * @param {string} measure
 */
export function leadQualityMeasureLabel(measure) {
  return hasTranslation(`reports.leadQualityMeasures.${measure}`) ? t(`reports.leadQualityMeasures.${measure}`) : measure;
}

const ENTITY_OF_MEASURE = Object.freeze({ lead: 'LEAD', family: 'FAMILY', guardian: 'GUARDIAN', student: 'STUDENT' });

/** @param {string} report @param {ReportBarItem} item @param {Record<string, any> | undefined} summary */
function itemLabel(report, item, summary) {
  if (report === 'DATA_QUALITY' && hasTranslation(`reports.dataQualityFields.${item.key}`)) return t(`reports.dataQualityFields.${item.key}`);
  if (report === 'LEAD_QUALITY_BY_ENTITY' && hasTranslation(`reports.leadQualityEntities.${item.key}`)) return t(`reports.leadQualityEntities.${item.key}`);
  if (report === 'LEAD_QUALITY_FIELDS' && hasTranslation(`reports.leadQualityMeasures.${item.key}`)) {
    const label = leadQualityMeasureLabel(item.key);
    const entity = /** @type {Record<string, string>} */ (ENTITY_OF_MEASURE)[item.key.split('.')[0]];
    // com todos os cadastros na mesma lista, o rótulo diz de quem é o campo ("Aluno · Data de nascimento")
    return summary?.entity === 'ALL' && entity ? `${t(`reports.leadQualityEntityPrefixes.${entity}`)} · ${label}` : label;
  }
  return item.label;
}

/** Listas em percentual cujo clique abre quem falta (a qualidade dos dados antiga e os cartões da qualidade do lead). */
const MISSING_ITEM_REPORTS = new Set(['DATA_QUALITY', 'LEAD_QUALITY_BY_ENTITY', 'LEAD_QUALITY_FIELDS', 'LEAD_QUALITY_BY_OWNER', 'LEAD_QUALITY_BY_STAGE']);

/** Relatórios cujas barras mostram o ponto com a cor da opção (etapa, etiqueta, tipo de evento). */
const COLORED_ITEM_REPORTS = new Set(['TAGS', 'STAGE_DISTRIBUTION', 'VISITS_BY_TYPE', 'LEAD_QUALITY_BY_STAGE']);

/**
 * Notas de rodapé dos rankings (sem série, sem escola de origem, outros fora da lista).
 * @param {{ report: string, summary: Record<string, any> | undefined }} props
 */
function ReportFootnotes({ report, summary }) {
  if (!summary) return null;
  const notes = [];
  if (hasTranslation(`reports.footnotes.${report}`) && (summary.missing !== undefined || summary.discarded !== undefined)) {
    notes.push(t(`reports.footnotes.${report}`, { missing: formatMetricValue(summary.missing ?? 0, 'NUMBER'), discarded: formatMetricValue(summary.discarded ?? 0, 'NUMBER') }));
  }
  if (report === 'OCCUPANCY_BY_CLASS' && summary.withoutSeats) notes.push(t('reports.footnotes.OCCUPANCY_BY_CLASS', { withoutSeats: formatMetricValue(summary.withoutSeats, 'NUMBER') }));
  if (summary.others) notes.push(t('reports.footnotes.others', { others: formatMetricValue(summary.others, 'NUMBER') }));
  // o que o papel de quem lê não vê sai do cartão; o rodapé avisa quantos
  if (summary.hiddenStats) notes.push(t('reports.footnotes.hiddenStats', { count: summary.hiddenStats }));
  if (summary.hiddenMeasures) notes.push(t('reports.footnotes.hiddenMeasures', { count: summary.hiddenMeasures }));
  if (notes.length === 0) return null;
  return html`<p class="report-footnote">${notes.join(' · ')}</p>`;
}

/** @param {{ data: BarListData }} props */
export function BarListWidget({ data }) {
  const maximum = data.scale === 'PERCENT' ? 100 : Math.max(1, ...data.items.map((item) => Number(item.value ?? 0)));
  return html`<div class="report-widget">
    <${ReportStats} stats=${data.stats ?? []} />
    <ul class="report-bars">
      ${data.items.map((item) => {
        const ratio = item.details?.find((detail) => detail.key === 'ratio');
        const onlyShare = item.details?.length === 1 && item.details[0].key === 'share' ? item.details[0] : null;
        // percentual com a fração junto ("2/3 · 66,7%") e participação junto da quantidade ("8 · 19%"): a lista fica compacta
        // sem base (turma sem vagas, campo que não vale para nenhum lead) não há percentual: diz isso em vez de "0/0"
        const withoutBase = ratio && item.format === 'PERCENT' && item.value === null && !ratio.total;
        const valueText = withoutBase
          ? t(data.report === 'OCCUPANCY_BY_CLASS' ? 'reports.details.noSeats' : 'reports.details.noBase')
          : ratio && item.format === 'PERCENT'
            ? `${formatReportDetails([ratio])} · ${formatMetricValue(item.value, 'PERCENT')}`
            : onlyShare ? `${formatMetricValue(item.value, item.format)} · ${formatMetricValue(onlyShare.value, 'PERCENT')}` : formatMetricValue(item.value, item.format);
        const otherDetails = (ratio && item.format === 'PERCENT') || onlyShare ? item.details?.filter((detail) => detail !== ratio && detail !== onlyShare) : item.details;
        const label = itemLabel(data.report, item, data.summary);
        const isDataQuality = MISSING_ITEM_REPORTS.has(data.report);
        const isOccupancy = data.report === 'OCCUPANCY_BY_CLASS';
        // a qualidade dos dados (e a do lead) abre os registros sem o dado; a ocupação abre as matrículas que ocupam vaga;
        // a visita por nível abre os alunos que visitaram e o total
        const missing = isDataQuality && ratio ? Math.max(0, Number(ratio.total ?? 0) - Number(ratio.value ?? 0)) : 0;
        const ariaValueText = isDataQuality ? t('dashboardDrilldown.missingValue', { count: missing })
          : isOccupancy && ratio ? t('dashboardDrilldown.occupiedSeats', { count: Number(ratio.value ?? 0) }) : formatMetricValue(item.value, item.format);
        const hint = isDataQuality ? t('dashboardDrilldown.missingHint') : isOccupancy ? t('dashboardDrilldown.occupiedSeatsHint') : undefined;
        const valueContent = ratio && item.format === 'PERCENT' && data.report === 'VISITS_BY_SEGMENT'
          ? html`<${DrillLink} point=${{ part: 'item', key: item.key, detailKey: 'ratio' }} label=${`${label} · ${t('dashboardDrilldown.series.ratio')}`}
              valueText=${formatMetricValue(ratio.value, 'NUMBER')} className="report-detail-link">${formatMetricValue(ratio.value, 'NUMBER')}<//>/<${DrillLink}
              point=${{ part: 'item', key: item.key, detailKey: 'total' }} label=${`${label} · ${t('dashboardDrilldown.series.total')}`}
              valueText=${formatMetricValue(ratio.total ?? 0, 'NUMBER')} className="report-detail-link">${formatMetricValue(ratio.total ?? 0, 'NUMBER')}<//>${' · '}${formatMetricValue(item.value, 'PERCENT')}`
          : undefined;
        return html`<${ReportBar}
          key=${item.key}
          label=${label}
          valueText=${valueText}
          valueContent=${valueContent}
          share=${(Number(item.value ?? 0) / maximum) * 100}
          tone=${item.tone}
          color=${COLORED_ITEM_REPORTS.has(data.report) ? item.color : undefined}
          point=${{ part: 'item', key: item.key }}
          ariaValueText=${ariaValueText}
          hint=${hint}
          details=${html`<${ReportDetailLinks} itemKey=${item.key} itemLabel=${label} details=${otherDetails} />`}
        />`;
      })}
    </ul>
    <${ReportFootnotes} report=${data.report} summary=${data.summary} />
  </div>`;
}

/**
 * Rótulo da faixa: dias ("8 a 14 dias", "+60 dias") ou percentual da nota ("Completos (100%)", "75% a 99%",
 * "Abaixo de 50%").
 * @param {DistributionBucket} bucket @param {'DAYS' | 'PERCENT_BAND'} [unit]
 */
export function bucketText(bucket, unit = 'DAYS') {
  // faixas com rótulo próprio do servidor (horas e dias do atendimento, 3.0.0)
  if (typeof bucket.label === 'string' && bucket.label !== '') return bucket.label;
  if (unit === 'PERCENT_BAND') {
    if (bucket.key === 'COMPLETE') return t('reports.buckets.percentComplete');
    if (bucket.from === 0) return t('reports.buckets.percentBelow', { to: Number(bucket.to) + 1 });
    return t('reports.buckets.percentRange', { from: bucket.from, to: bucket.to });
  }
  return bucket.to === null
    ? t('reports.buckets.over', { days: bucket.from - 1 })
    : t('reports.buckets.range', { from: bucket.from, to: bucket.to });
}

/** @param {{ data: DistributionData }} props */
export function DistributionWidget({ data }) {
  const maximum = Math.max(1, ...data.buckets.map((bucket) => bucket.value));
  return html`<div class="report-widget">
    <${ReportStats} stats=${data.stats ?? []} />
    <ul class="report-bars">
      ${data.buckets.map((bucket) => html`<${ReportBar}
        key=${bucket.key}
        label=${bucketText(bucket, data.unit)}
        valueText=${`${formatMetricValue(bucket.value, 'NUMBER')} · ${formatMetricValue(bucket.share, 'PERCENT')}`}
        ariaValueText=${formatMetricValue(bucket.value, 'NUMBER')}
        share=${(bucket.value / maximum) * 100}
        tone=${bucket.value > 0 ? bucket.tone : 'NEUTRAL'}
        point=${{ part: 'bucket', key: bucket.key }}
      />`)}
    </ul>
    <${ReportFootnotes} report=${data.report} summary=${data.summary} />
  </div>`;
}

/**
 * Funil com degraus de tempo à esquerda, abas Todos/Ativos/Fechados e alternância entre quantidade e valor. Cada linha,
 * os ganhos, os perdidos e "Ver todos" abrem a lista da aba aberta; no valor, a mesma lista como soma.
 * @param {{ data: FunnelFlowData }} props
 */
export function FunnelFlowWidget({ data }) {
  const [tabKey, setTabKey] = useState(/** @type {'ALL' | 'ACTIVE' | 'CLOSED'} */ ('ALL'));
  const [measure, setMeasure] = useState(/** @type {'COUNT' | 'AMOUNT'} */ ('COUNT'));
  const drill = useDrilldown();
  const tab = data.tabs[tabKey] ?? data.tabs.ALL;
  const isAmount = measure === 'AMOUNT';
  const base = isAmount ? tab.totalAmount : tab.total;
  const maximumDays = Math.max(1, ...tab.rows.map((row) => row.averageDays ?? 0));
  const stepCount = Math.max(1, tab.rows.length);
  /** @param {number} count @param {number} amount */
  const valueOf = (count, amount) => (isAmount ? formatMetricValue(amount, 'CURRENCY', { compact: true }) : formatMetricValue(count, 'NUMBER'));
  /** @param {number} count @param {number} amount */
  const shareOf = (count, amount) => (base > 0 ? ((isAmount ? amount : count) / base) * 100 : 0);
  /** @param {import('./drilldown.js').DrilldownPoint} point */
  const withMeasure = (point) => (isAmount ? { ...point, metric: 'AMOUNT' } : point);
  const totalPoint = withMeasure({ part: 'funnelTotal', tab: tabKey });
  return html`<div class="report-widget report-funnel">
    <div class="report-funnel-top">
      <div class="report-funnel-controls">
        <div class="report-tabs" role="tablist">
          ${(/** @type {const} */ (['ALL', 'ACTIVE', 'CLOSED'])).map((key) => html`<button type="button" role="tab" key=${key} class="report-tab"
            aria-selected=${tabKey === key ? 'true' : 'false'} onClick=${() => setTabKey(key)}>${t(`reports.funnel.tabs.${key}`)}
            <span class="report-tab-count">${formatMetricValue(data.tabs[key]?.total ?? 0, 'NUMBER')}</span></button>`)}
        </div>
        <div class="report-tabs report-tabs-compact" role="tablist">
          ${(/** @type {const} */ (['COUNT', 'AMOUNT'])).map((key) => html`<button type="button" role="tab" key=${key} class="report-tab"
            aria-selected=${measure === key ? 'true' : 'false'} onClick=${() => setMeasure(key)}>${t(`reports.funnel.measures.${key}`)}</button>`)}
        </div>
      </div>
      <div class="report-stat report-funnel-lifecycle" data-tone="VISIT">
        <span class="report-stat-value">${formatMetricValue(tab.lifecycleDays, 'DAYS')}</span>
        <span class="report-stat-label">${t('reports.funnel.lifecycle')}</span>
      </div>
    </div>
    <div class="report-funnel-header" aria-hidden="true">
      <span>${t('reports.funnel.timeInStage')}</span><span>${t('reports.funnel.stage')}</span><span>${t('reports.funnel.reached')}</span>
    </div>
    <ol class="report-funnel-rows">
      ${tab.rows.map((row, index) => {
        const point = withMeasure({ part: 'funnelRow', tab: tabKey, key: row.key });
        return html`<li class="report-funnel-row" key=${row.key} data-drillable=${drill?.target(point) ? 'true' : undefined}>
          <div class="report-funnel-step" title=${row.averageDays === null ? t('reports.funnel.noHistory') : formatMetricValue(row.averageDays, 'DAYS')}>
            <span class="report-funnel-step-block" data-empty=${row.averageDays === null ? 'true' : undefined}
              style=${{ marginLeft: `${(index / stepCount) * 45}%`, width: `${row.averageDays === null ? 18 : Math.max(18, (row.averageDays / maximumDays) * 55)}%` }}>
              ${row.averageDays === null ? '-' : formatMetricValue(row.averageDays, 'DAYS')}
            </span>
          </div>
          <span class="report-funnel-stage"><${Tag} text=${row.label} color=${row.color ?? 'gray'} /></span>
          <div class="report-funnel-bar" data-tone=${index === 0 ? 'VOLUME' : 'VISIT'}>
            <div class="report-bar-track"><span class="report-bar-fill" style=${{ width: `${Math.min(100, shareOf(row.reached, row.reachedAmount))}%` }}></span></div>
            <${DrillLink} point=${point} label=${row.label} valueText=${valueOf(row.reached, row.reachedAmount)} className="report-funnel-value" stretched=${true}>
              <strong>${formatMetricValue(shareOf(row.reached, row.reachedAmount), 'PERCENT')}</strong> ${valueOf(row.reached, row.reachedAmount)}
            <//>
          </div>
        </li>`;
      })}
      ${[['won', 'WON', tab.won], ['lost', 'ALERT', tab.lost]].map(([key, tone, entry]) => {
        const values = /** @type {CountAndAmount} */ (entry);
        const point = withMeasure({ part: 'funnelOutcome', tab: tabKey, key: /** @type {string} */ (key) });
        const label = values.label ?? t(`reports.funnel.${key}`);
        return html`<li class="report-funnel-row report-funnel-final" key=${key} data-drillable=${drill?.target(point) ? 'true' : undefined}>
          <div class="report-funnel-step"></div>
          <span class="report-funnel-stage report-funnel-final-label" data-tone=${tone}>${label}</span>
          <div class="report-funnel-bar" data-tone=${tone}>
            <div class="report-bar-track"><span class="report-bar-fill" style=${{ width: `${Math.min(100, shareOf(values.count, values.amount))}%` }}></span></div>
            <${DrillLink} point=${point} label=${label} valueText=${valueOf(values.count, values.amount)} className="report-funnel-value" stretched=${true}>
              <strong>${formatMetricValue(shareOf(values.count, values.amount), 'PERCENT')}</strong> ${valueOf(values.count, values.amount)}
            <//>
          </div>
        </li>`;
      })}
    </ol>
    ${drill?.target(totalPoint)
      ? html`<div class="report-see-all">
          <${DrillLink} point=${totalPoint} label=${t(`reports.funnel.tabs.${tabKey}`)} valueText=${valueOf(tab.total, tab.totalAmount)} className="report-see-all-link">
            <span>${t('dashboardDrilldown.seeAllCount', { count: tab.total })}</span><${Icon} name="IconArrowUpRight" size=${14} />
          <//>
        </div>`
      : null}
  </div>`;
}

/**
 * Colunas por etapa com rolagem horizontal: dentro agora, entradas e perdas no período. A quantidade de cada bloco abre
 * a lista; o valor do bloco abre a mesma lista como soma.
 * @param {{ data: StageFlowData }} props
 */
export function StageFlowWidget({ data }) {
  /** @param {StageFlowColumn} column @param {'inside' | 'entered' | 'lost'} key @param {CountAndAmount} values @param {string} tone */
  const block = (column, key, values, tone) => {
    const countPoint = { part: 'stageColumn', key: column.key, block: key };
    const label = `${column.label} · ${t(`reports.stageFlow.${key}`)}`;
    return html`<div class="report-flow-block" data-tone=${tone}>
      <span class="report-flow-block-label">${t(`reports.stageFlow.${key}`)}</span>
      <${DrillLink} point=${countPoint} label=${label} valueText=${formatMetricValue(values.count, 'NUMBER')} className="report-flow-block-count" stretched=${true}>
        ${formatMetricValue(values.count, 'NUMBER')}
      <//>
      <${DrillLink} point=${{ ...countPoint, metric: 'AMOUNT' }} label=${label} valueText=${formatMetricValue(values.amount, 'CURRENCY')}
        className="report-flow-block-amount">${formatMetricValue(values.amount, 'CURRENCY', { compact: true })}<//>
    </div>`;
  };
  return html`<div class="report-widget report-flow">
    <div class="report-flow-columns">
      ${data.columns.map((column) => html`<section class="report-flow-column" key=${column.key}
        data-highlight=${column.key === data.highlightStage ? 'true' : undefined}
        aria-label=${column.label}>
        <header class="report-flow-column-header">
          <${Tag} text=${column.label} color=${column.color ?? 'gray'} />
          ${column.key === data.highlightStage ? html`<span class="report-flow-highlight">${t('reports.stageFlow.highlight')}</span>` : null}
        </header>
        ${block(column, 'inside', column.inside, 'VOLUME')}
        ${block(column, 'entered', column.entered, 'VISIT')}
        ${block(column, 'lost', column.lost, 'ALERT')}
      </section>`)}
    </div>
  </div>`;
}
